/**
 * `GET /dispatch/tasa-victoria` — CA-05.9 / `02` §9.2bis, el instrumento que dice si el criterio
 * de broadcast está cumpliendo la función objetivo del negocio (ver el comentario largo en
 * `apps/api/src/dispatch/tasa-victoria.service.ts`).
 *
 * Tipos leídos de `TasaVictoriaConductor`/`ResumenTasaVictoria`/`InformeTasaVictoria` en ese
 * archivo y VERIFICADOS por efecto contra un backend real (`curl` con un token `Bearer`, ver el
 * reporte de la sesión) — con `resumen`+`porConductor` vacíos (sin datos) y con la forma exacta
 * de fechas serializadas (`Date` → ISO string sobre HTTP).
 *
 * ⚠️ **NO trae `conductorNombre`** — a diferencia del mock `RepartoConductor` de
 * `dominio/contratos.ts`, sólo trae `conductorId`. No existe hoy ningún endpoint que devuelva un
 * listado general de conductores con nombre (el único listado con nombres es la cola de
 * verificación, acotada a conductores EN_REVISION — un conductor con ofertas ya está aprobado, así
 * que ese cruce ni siquiera aplicaría). Se muestra el id; ver la nota en `Dispatch.tsx`.
 */
import { obtener } from './clienteApi';

/**
 * `02` §9.2bis: el diagnóstico está CALCULADO por el backend, no derivado en la pantalla — leer
 * el comentario de `tasa-victoria.service.ts` antes de "traducir" estos cuatro valores distinto.
 */
export type DiagnosticoTasaVictoria =
  'SIN_MUESTRA' | 'SIN_DEMANDA' | 'REPARTO_CONCENTRADO' | 'NORMAL';

export interface ResumenTasaVictoria {
  desde: string;
  hasta: string;
  /** Conductores con al menos una oferta en la ventana. */
  conductores: number;
  /** De esos, los que además COMPITIERON al menos una vez (denominador > 0). */
  conMuestra: number;
  ofertasTotales: number;
  ganadasTotales: number;
  /** `null` si `conMuestra === 0`. */
  medianaTasa: number | null;
  /** Percentil 10 — la cola de abajo, lo que se compara contra la mediana. */
  p10Tasa: number | null;
  diagnostico: DiagnosticoTasaVictoria;
}

export interface TasaVictoriaConductor {
  conductorId: string;
  ofertasRecibidas: number;
  ganadas: number;
  perdidas: number;
  expiradas: number;
  rechazadas: number;
  /** `ganadas + perdidas` — el denominador real de la tasa. */
  competidas: number;
  /** `null` cuando `competidas === 0`: distingue "nunca compitió" de "compitió y perdió todas". */
  tasaVictoria: number | null;
}

export interface InformeTasaVictoria {
  resumen: ResumenTasaVictoria;
  porConductor: TasaVictoriaConductor[];
}

/**
 * Sin `desde`/`hasta` el backend usa los últimos 7 días (su propio default). Se deja igual acá:
 * no hay todavía un selector de rango en la pantalla, y adivinar un rango distinto al del
 * backend sería una segunda fuente de verdad para la misma pregunta.
 */
export const obtenerTasaVictoria = (): Promise<InformeTasaVictoria> =>
  obtener<InformeTasaVictoria>('/dispatch/tasa-victoria');
